export type ApiError = Error & { status?: number; retryAfterSeconds?: number };

export const SERVER_ERROR_MESSAGE =
  "Server error. Please contact your administrator if the problem persists";

export function collectClientDebugContext(): Record<string, string> {
  const context: Record<string, string> = {
    Time: new Date().toISOString(),
  };
  if (typeof window !== "undefined") {
    context.Page = window.location.href;
    context.Online = navigator.onLine ? "yes" : "no";
  }
  const appVersion = import.meta.env.VITE_APP_VERSION;
  if (typeof appVersion === "string" && appVersion.trim()) {
    context["App version"] = appVersion.trim();
  }
  return context;
}

export function appendSupportDebugBlock(
  message: string,
  details: Record<string, string | number | boolean | null | undefined>
): string {
  const entries = Object.entries(details).filter(
    ([, value]) => value !== null && value !== undefined && value !== ""
  );
  if (entries.length === 0) {
    return message;
  }
  const bullets = entries.map(([key, value]) => `• ${key}: ${value}`).join("\n");
  return `${message}\n\nShare with your administrator:\n${bullets}`;
}

export function formatNetworkFetchErrorMessage(
  requestUrl: string,
  lastErr: unknown,
  attempts: number
): string {
  const browserError =
    lastErr instanceof Error ? lastErr.message : lastErr != null ? String(lastErr) : "unknown";
  return appendSupportDebugBlock(
    "Could not reach Labwick. Please check your connection and try again.",
    {
      ...collectClientDebugContext(),
      API: requestUrl,
      Error: browserError,
      Retries: attempts,
    }
  );
}

export function formatHttpErrorWithDebug(
  userMessage: string,
  requestUrl: string,
  details: Record<string, string | number | boolean | null | undefined> = {}
): string {
  return appendSupportDebugBlock(userMessage, {
    ...collectClientDebugContext(),
    API: requestUrl,
    ...details,
  });
}

export function isFetchNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) {
    return false;
  }
  const msg = String((error as Error).message || "");
  return msg === "Failed to fetch" || msg.includes("fetch") || msg.includes("NetworkError");
}

/**
 * Retry transient browser/network fetch failures (stale connections, brief offline blips).
 */
export async function fetchWithNetworkRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
  options?: { attempts?: number; baseDelayMs?: number }
): Promise<Response> {
  const attempts = options?.attempts ?? 3;
  const baseDelayMs = options?.baseDelayMs ?? 400;
  const requestInit: RequestInit = { ...init, cache: init?.cache ?? "no-store" };
  let lastErr: unknown;

  for (let i = 0; i < attempts; i++) {
    try {
      return await fetch(input, requestInit);
    } catch (err) {
      lastErr = err;
      if (!isFetchNetworkError(err) || i === attempts - 1) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, baseDelayMs * (i + 1)));
    }
  }

  if (isFetchNetworkError(lastErr)) {
    const requestUrl = typeof input === "string" ? input : input instanceof URL ? input.href : String(input);
    const message = formatNetworkFetchErrorMessage(requestUrl, lastErr, attempts);
    console.error("Network fetch failed after retries:", { requestUrl, attempts, lastErr });
    throw createApiError(message);
  }
  throw lastErr;
}

export const createApiError = (message: string, status?: number, retryAfterSeconds?: number): ApiError => {
  const error = new Error(message) as ApiError;
  if (typeof status === "number") {
    error.status = status;
  }
  if (typeof retryAfterSeconds === "number") {
    error.retryAfterSeconds = retryAfterSeconds;
  }
  return error;
};

export const createServerError = (status?: number): ApiError => {
  return createApiError(SERVER_ERROR_MESSAGE, status);
};

export function formatAuthRateLimitMessage(retryAfterSeconds: number): string {
  const s = Math.max(1, Math.round(retryAfterSeconds));
  if (s <= 90) {
    return `Too many attempts. Please wait ${s} seconds before trying again.`;
  }
  const mins = Math.ceil(s / 60);
  if (mins <= 1) {
    return "Too many attempts. Please wait about a minute before trying again.";
  }
  return `Too many attempts. Please wait about ${mins} minutes before trying again.`;
}
